import React from 'react';
import { ResourceTile, ResourceTileSkeleton } from '../resource-tile';
import { Resource } from '../../hooks/api/useResources';
import { PEERSONA_RESOURCES_VISIBLE } from '../../hooks/api/usePeersonaResources';
import { PEERSONA_DIVIDER, PEERSONA_TEXT_TERTIARY } from './peersona-styles';

interface PeersonaResourcesPanelProps {
  open: boolean;
  resources: Resource[];
  isLoading: boolean;
  isResorting: boolean;
  onClose: () => void;
}

/**
 * Shared organizational-resources drawer (Peersona + Fi / KINDLY-1117).
 * Ranks via ``POST /peersona/resources/rank`` against conversation context.
 */
export const PeersonaResourcesPanel: React.FC<PeersonaResourcesPanelProps> = ({
  open,
  resources,
  isLoading,
  isResorting,
  onClose,
}) => {
  if (!open) return null;

  const showSkeletons = isLoading;
  const showResortingCopy = isLoading || isResorting;
  const visibleResources = resources.slice(0, PEERSONA_RESOURCES_VISIBLE);

  return (
    <div
      className="flex flex-col border-t bg-white shadow-sm"
      style={{ borderColor: PEERSONA_DIVIDER }}
      aria-label="Organization resources"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <p
          className={
            showResortingCopy
              ? 'font-open-sans text-sm italic text-gray-500'
              : 'font-albert-sans text-sm font-semibold text-black'
          }
          aria-live={showResortingCopy ? 'polite' : undefined}
        >
          {showResortingCopy
            ? 'Resorting resources based on your conversation…'
            : 'Relevant resources available to you'}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="font-open-sans text-xs font-semibold hover:underline"
          style={{ color: PEERSONA_TEXT_TERTIARY }}
        >
          Close
        </button>
      </div>

      <div className="px-3 pb-3">
        {resources.length === 0 && !showSkeletons ? (
          <p className="font-open-sans text-sm text-gray-500">No resources available for your organization.</p>
        ) : (
          <>
            <div className="grid w-full grid-cols-4 gap-2">
              {showSkeletons
                ? Array.from({ length: PEERSONA_RESOURCES_VISIBLE }, (_, index) => (
                    <div key={`skeleton-${index}`} className="min-w-0">
                      <ResourceTileSkeleton compact />
                    </div>
                  ))
                : visibleResources.map((resource) => (
                    <div key={resource.id} className="min-w-0">
                      <ResourceTile compact resource={resource} />
                    </div>
                  ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
